/**
 * Google Analytics Utility Functions
 * Tracks page views and custom events
 * 
 * Author: Markus van Kempen - markus.van.kempen@gmail.com
 * Created: October 29, 2025
 */

import { logProductViewActivity, logAddToCartActivity } from './userActivity';

// Track page views
export const trackPageView = (url, title) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-3701ER9NSJ', {
      page_path: url,
      page_title: title
    });
  }
};

// Track custom events
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

// Track e-commerce events
export const trackProductView = (product) => {
  trackEvent('view_item', {
    currency: 'CAD',
    value: product.price_cents / 100,
    items: [{
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      price: product.price_cents / 100
    }]
  });
  logProductViewActivity(product);
};

export const trackAddToCart = (product, quantity = 1) => {
  trackEvent('add_to_cart', {
    currency: 'CAD',
    value: (product.price_cents / 100) * quantity,
    items: [{
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      quantity: quantity,
      price: product.price_cents / 100
    }]
  });
  logAddToCartActivity(product, quantity);
};

export const trackRemoveFromCart = (product, quantity = 1) => {
  trackEvent('remove_from_cart', {
    currency: 'CAD',
    value: (product.price_cents / 100) * quantity,
    items: [{
      item_id: product.id,
      item_name: product.name,
      quantity: quantity,
      price: product.price_cents / 100
    }]
  });
};

export const trackBeginCheckout = (cartItems, totalValue) => {
  trackEvent('begin_checkout', {
    currency: 'CAD',
    value: totalValue,
    items: cartItems.map(item => ({
      item_id: item.id,
      item_name: item.name,
      item_category: item.category,
      quantity: item.quantity,
      price: item.price_cents / 100
    }))
  });
};

export const trackPurchase = (orderId, cartItems, totalValue) => {
  trackEvent('purchase', {
    transaction_id: orderId,
    currency: 'CAD',
    value: totalValue,
    items: cartItems.map(item => ({
      item_id: item.id,
      item_name: item.name,
      item_category: item.category,
      quantity: item.quantity,
      price: item.price_cents / 100
    }))
  });
};

// Track search
export const trackSearch = (searchTerm) => {
  trackEvent('search', {
    search_term: searchTerm
  });
};

// Track user engagement
export const trackDownload = (fileName) => {
  trackEvent('file_download', {
    file_name: fileName
  });
};

export const trackVideoPlay = (videoTitle) => {
  trackEvent('video_play', {
    video_title: videoTitle
  });
};

export const trackOutboundLink = (url) => {
  trackEvent('outbound_link', {
    link_url: url
  });
};

const analytics = {
  trackPageView,
  trackEvent,
  trackProductView,
  trackAddToCart,
  trackRemoveFromCart,
  trackBeginCheckout,
  trackPurchase,
  trackSearch,
  trackDownload,
  trackVideoPlay,
  trackOutboundLink
};

export default analytics;

